/**
 * AWS Cognito Service for Admin Panel
 * Web-compatible version using localStorage instead of SecureStore
 */

import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  GetUserCommand,
  GlobalSignOutCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  AuthFlowType,
} from '@aws-sdk/client-cognito-identity-provider';

// AWS Cognito Configuration (same as mobile app)
const REGION = 'us-east-1';
const CLIENT_ID = '12qec3fdnqilpc8kinrrhm074o';

// Initialize Cognito Client
const cognitoClient = new CognitoIdentityProviderClient({
  region: REGION,
});

export interface SignInResult {
  success: boolean;
  tokens?: {
    accessToken: string;
    idToken: string;
    refreshToken: string;
  };
  error?: string;
  errorCode?: string;
}

export interface SignUpResult {
  success: boolean;
  userSub?: string;
  error?: string;
  errorCode?: string;
}

export interface ConfirmResult {
  success: boolean;
  error?: string;
  errorCode?: string;
}

class CognitoService {
  /**
   * Sign in a user with email and password
   * Using USER_PASSWORD_AUTH flow
   */
  async signIn(username: string, password: string): Promise<SignInResult> {
    try {
      const command = new InitiateAuthCommand({
        ClientId: CLIENT_ID,
        AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      });

      const response = await cognitoClient.send(command);

      // Check if user needs to complete a challenge
      if (response.ChallengeName) {
        return {
          success: false,
          error: `Challenge required: ${response.ChallengeName}`,
        };
      }

      // Successful authentication
      if (response.AuthenticationResult) {
        const tokens = {
          accessToken: response.AuthenticationResult.AccessToken!,
          idToken: response.AuthenticationResult.IdToken!,
          refreshToken: response.AuthenticationResult.RefreshToken!,
        };

        // Store tokens in localStorage (web-compatible)
        this.storeTokens(tokens);

        return {
          success: true,
          tokens,
        };
      }

      return {
        success: false,
        error: 'Authentication failed',
      };
    } catch (error: any) {
      console.log('Sign in error:', error);

      if (error.name === 'UserNotConfirmedException') {
        return {
          success: false,
          error: 'User not confirmed',
          errorCode: error.name,
        };
      } else if (error.name === 'UserNotFoundException') {
        return {
          success: false,
          error: 'User not found',
          errorCode: error.name,
        };
      } else if (error.name === 'NotAuthorizedException') {
        return {
          success: false,
          error: 'Invalid credentials',
          errorCode: error.name,
        };
      }

      return {
        success: false,
        error: error.message || 'Sign in failed',
        errorCode: error.name,
      };
    }
  }

  /**
   * Sign up a new user
   * Note: Cognito is configured to use phone number as username
   */
  async signUp(
    phoneNumber: string,
    password: string,
    firstName: string,
    lastName: string,
    email: string
  ): Promise<SignUpResult> {
    try {
      const userAttributes = [
        { Name: 'email', Value: email },
        { Name: 'phone_number', Value: phoneNumber },
        { Name: 'name', Value: firstName },
        { Name: 'family_name', Value: lastName },
      ];

      const command = new SignUpCommand({
        ClientId: CLIENT_ID,
        Username: phoneNumber, // Phone number is the username
        Password: password,
        UserAttributes: userAttributes,
      });

      const response = await cognitoClient.send(command);

      return {
        success: true,
        userSub: response.UserSub,
      };
    } catch (error: any) {
      console.log('Sign up error:', error);

      if (error.name === 'UsernameExistsException') {
        return {
          success: false,
          error: 'An account with this phone number already exists',
          errorCode: 'USER_EXISTS',
        };
      } else if (error.name === 'InvalidPasswordException') {
        return {
          success: false,
          error: 'Password does not meet requirements',
          errorCode: 'INVALID_PASSWORD',
        };
      } else if (error.name === 'InvalidParameterException') {
        return {
          success: false,
          error: error.message || 'Invalid parameters',
          errorCode: 'INVALID_PARAMETER',
        };
      }

      return {
        success: false,
        error: error.message || 'Sign up failed',
        errorCode: error.name,
      };
    }
  }

  /**
   * Confirm user registration with verification code
   */
  async confirmSignUp(phoneNumber: string, code: string): Promise<ConfirmResult> {
    try {
      const command = new ConfirmSignUpCommand({
        ClientId: CLIENT_ID,
        Username: phoneNumber,
        ConfirmationCode: code,
      });

      await cognitoClient.send(command);

      return { success: true };
    } catch (error: any) {
      console.log('Confirm sign up error:', error);

      if (error.name === 'CodeMismatchException') {
        return {
          success: false,
          error: 'Invalid verification code',
          errorCode: 'INVALID_CODE',
        };
      } else if (error.name === 'ExpiredCodeException') {
        return {
          success: false,
          error: 'Verification code has expired',
          errorCode: 'EXPIRED_CODE',
        };
      } else if (error.name === 'LimitExceededException') {
        return {
          success: false,
          error: 'Too many attempts. Please try again later.',
          errorCode: 'TOO_MANY_ATTEMPTS',
        };
      }

      return {
        success: false,
        error: error.message || 'Verification failed',
        errorCode: error.name,
      };
    }
  }

  /**
   * Resend verification code
   */
  async resendConfirmationCode(phoneNumber: string): Promise<ConfirmResult> {
    try {
      const command = new ResendConfirmationCodeCommand({
        ClientId: CLIENT_ID,
        Username: phoneNumber,
      });

      await cognitoClient.send(command);

      return { success: true };
    } catch (error: any) {
      console.log('Resend code error:', error);

      if (error.name === 'LimitExceededException') {
        return {
          success: false,
          error: 'Too many attempts. Please try again later.',
          errorCode: 'TOO_MANY_ATTEMPTS',
        };
      }

      return {
        success: false,
        error: error.message || 'Failed to resend code',
        errorCode: error.name,
      };
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshSession(): Promise<SignInResult> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');

      if (!refreshToken) {
        return {
          success: false,
          error: 'No refresh token available',
        };
      }

      const command = new InitiateAuthCommand({
        ClientId: CLIENT_ID,
        AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
        },
      });

      const response = await cognitoClient.send(command);

      if (response.AuthenticationResult) {
        const tokens = {
          accessToken: response.AuthenticationResult.AccessToken!,
          idToken: response.AuthenticationResult.IdToken!,
          refreshToken: refreshToken, // Refresh token doesn't change
        };

        // Update stored tokens
        this.storeTokens(tokens);

        return {
          success: true,
          tokens,
        };
      }

      return {
        success: false,
        error: 'Failed to refresh session',
      };
    } catch (error: any) {
      console.log('Refresh session error:', error);
      return {
        success: false,
        error: error.message || 'Failed to refresh session',
      };
    }
  }

  /**
   * Get user attributes
   */
  async getUserAttributes(): Promise<any> {
    try {
      const accessToken = localStorage.getItem('access_token');

      if (!accessToken) {
        throw new Error('No access token available');
      }

      const command = new GetUserCommand({
        AccessToken: accessToken,
      });

      const response = await cognitoClient.send(command);

      const attributes: any = {};
      response.UserAttributes?.forEach((attr) => {
        attributes[attr.Name!] = attr.Value;
      });

      return attributes;
    } catch (error: any) {
      console.log('Get user attributes error:', error);

      // If token is expired, try to refresh
      if (error.name === 'NotAuthorizedException') {
        const refreshResult = await this.refreshSession();
        if (refreshResult.success) {
          // Retry with new token
          return this.getUserAttributes();
        }
      }

      throw error;
    }
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    try {
      const accessToken = localStorage.getItem('access_token');

      if (accessToken) {
        // Try to do a global sign out
        const command = new GlobalSignOutCommand({
          AccessToken: accessToken,
        });

        try {
          await cognitoClient.send(command);
        } catch (error) {
          // Ignore errors on sign out
          console.log('Global sign out error (ignored):', error);
        }
      }
    } catch (error) {
      console.log('Sign out error:', error);
    } finally {
      // Clear all stored data
      this.clearTokens();
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const accessToken = localStorage.getItem('access_token');
      const idToken = localStorage.getItem('id_token');

      if (!accessToken || !idToken) {
        return false;
      }

      // Parse the token to check expiration
      const tokenParts = accessToken.split('.');
      if (tokenParts.length !== 3) {
        return false;
      }

      // Decode the payload (web-compatible)
      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      // Check if token is expired
      if (payload.exp && payload.exp < currentTime) {
        // Try to refresh the token
        const refreshResult = await this.refreshSession();
        return refreshResult.success;
      }

      return true;
    } catch (error) {
      console.log('Auth check error:', error);
      return false;
    }
  }

  /**
   * Store tokens in localStorage
   */
  private storeTokens(tokens: { accessToken: string; idToken: string; refreshToken: string }) {
    localStorage.setItem('access_token', tokens.accessToken);
    localStorage.setItem('id_token', tokens.idToken);
    localStorage.setItem('refresh_token', tokens.refreshToken);
    localStorage.setItem('admin_authenticated', 'true');
  }

  /**
   * Clear stored tokens
   */
  private clearTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('admin_authenticated');
    localStorage.removeItem('admin_user');
  }

  /**
   * Get stored tokens
   */
  getTokens(): { accessToken: string | null; idToken: string | null; refreshToken: string | null } {
    const accessToken = localStorage.getItem('access_token');
    const idToken = localStorage.getItem('id_token');
    const refreshToken = localStorage.getItem('refresh_token');

    return {
      accessToken,
      idToken,
      refreshToken,
    };
  }

  /**
   * Get current access token (for Apollo Client)
   */
  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  /**
   * Get current ID token
   */
  getIdToken(): string | null {
    return localStorage.getItem('id_token');
  }
}

export default new CognitoService();
